import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp,
  Users,
  Award,
  Bell,
  Star,
  Euro,
  MapPin,
  Mail,
  AlertTriangle,
  Calendar,
  FileText,
  ChevronRight,
  ChevronLeft,
  X,
  Phone,
  Building2,
  MessageCircle
} from 'lucide-react';
import { supabaseStatsStorage, supabaseBookingDatesStorage, supabaseBookingStorage } from '../services/supabaseStorage';
import { supabase } from '../lib/supabase';
import { DEFAULT_TENANT_ID } from '../lib/defaultTenant';
import { BRAND } from '../lib/brand';
import { interpretersService } from '../services/interpretersService';
import { DashboardStats } from '../types/crm';
import { CalendarEvent, Location } from '../types/crm';
import { mapLocationDbRow } from '../utils/locationDbRow';
import { useToast } from '../contexts/ToastContext';
import { useSmartRealtime } from '../hooks/useSmartRealtime';
import { Booking } from '../types/crm';
import { isAttendeeCountedInBookingTotal } from '../utils/sessionAttendeeStatus';
import { fetchCourseFeeOverrideMap } from '../services/coursesService';
import { getCourseCatalogDefaultFeeForSession } from '../utils/courseCatalogFee';
import { TrainingCalendarEventCard } from './dashboard/TrainingCalendarEventCard';
import { DayScheduleTable } from './dashboard/DayScheduleTable';
import {
  formatBlockedDayCalendarLabel,
  blockedDayCellClasses,
  isoDayKey,
  maxVisibleEvents,
  linesBudgetForCard,
  bookingReferenceCode,
  sessionReferenceCode,
  shortVenueLabel,
  eachDayInIsoRange,
  endOfMonth,
  formatShortUkDate,
  hasActiveScheduleDateRange,
  mondayOfWeekContaining,
  noonDate,
  parseIsoDay,
  sortCalendarEventsByDateTime,
  startOfMonth,
} from './dashboard/trainingCalendarUtils';
import { ManageEmailTemplatesModal } from './crm/ManageEmailTemplatesModal';
import { ManageWhatsAppTemplatesModal } from './crm/ManageWhatsAppTemplatesModal';

const formatNumber = (num: number) => {
  if (num === undefined || num === null) {
    return '0';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num.toString();
};

interface DashboardModuleProps {
  onViewChange: (view: 'dashboard' | 'certificates' | 'clients' | 'reminders' | 'reviews' | 'bookings' | 'quotes' | 'superadmin' | 'admin' | 'faq' | 'tasks' | 'financial' | 'emails' | 'ops', context?: any) => void;
  onNavigateToClient?: (customerId: string) => void;
  onOpenSession?: (sessionId: string) => void;
}

type CalendarView = 'month' | 'week' | 'day';

export function DashboardModule({ onViewChange, onNavigateToClient, onOpenSession }: DashboardModuleProps) {
  const [stats, setStats] = useState<DashboardStats>({
    totalCustomers: 0,
    totalCertificates: 0,
    activeReminders: 0,
    dueReviewReminders: 0,
    totalLocations: 0,
    totalContacts: 0,
    unpaidCustomers: 0,
    totalBookings: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [showEmailTemplatesModal, setShowEmailTemplatesModal] = useState(false);
  const [showWhatsAppTemplatesModal, setShowWhatsAppTemplatesModal] = useState(false);
  const [defaultLocations, setDefaultLocations] = useState<Location[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const { showError, showSuccess } = useToast();
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);
  /** Day selected from "+ n more" in month/week calendar */
  const [calendarDayListModal, setCalendarDayListModal] = useState<{ date: Date; events: CalendarEvent[] } | null>(null);
  const [dayListModalSelectedId, setDayListModalSelectedId] = useState<string | null>(null);
  /** Month/Week/Range: show schedule table instead of calendar grid */
  const [calendarScheduleMode, setCalendarScheduleMode] = useState(false);
  /** Monday of the week currently shown in Week view (and Day view anchor). */
  const [weekStartDate, setWeekStartDate] = useState(() => mondayOfWeekContaining(new Date()));
  const [blockedDaysByDate, setBlockedDaysByDate] = useState<Record<string, { id: string; severity: 'warn' | 'block'; reason: string }>>({});
  const [blockedDayModal, setBlockedDayModal] = useState<{ date: Date; severity: 'warn' | 'block'; reason: string; existingId?: string } | null>(null);
  /** Optional date-range filter applied to the Training Schedule. When set,
   *  `getEventsForDay` hides sessions outside of [from, to]. Replaces the old
   *  "View All Bookings" button in the calendar header (see list-view UX). */
  const [scheduleDateRange, setScheduleDateRange] = useState<{ from: string; to: string }>({ from: '', to: '' });

  /** Debounce stats reload from realtime (certificate_reminders fires often). */
  const silentStatsReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Debounce calendar reload when session/attendee rows change. */
  const calendarReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleCalendarReload = () => {
    if (calendarReloadTimerRef.current) clearTimeout(calendarReloadTimerRef.current);
    calendarReloadTimerRef.current = setTimeout(() => {
      calendarReloadTimerRef.current = null;
      void loadCalendarEvents(defaultLocations);
    }, 400);
  };

  const loadDashboardData = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setIsLoading(true);
      const dashboardStats = await supabaseStatsStorage.getDashboardStats();
      setStats(dashboardStats);
    } catch (error) {
      showError('Error loading dashboard data');
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const scheduleSilentDashboardStatsReload = () => {
    if (silentStatsReloadTimerRef.current) clearTimeout(silentStatsReloadTimerRef.current);
    silentStatsReloadTimerRef.current = setTimeout(() => {
      silentStatsReloadTimerRef.current = null;
      void loadDashboardData({ silent: true });
    }, 500);
  };

  useEffect(() => {
    const initializeData = async () => {
      // Load stats and locations in parallel, defer calendar (heavy)
      const [, defaultLocs] = await Promise.all([
        loadDashboardData(),
        loadDefaultLocations()
      ]);
      
      // Set locations immediately
      if (defaultLocs.length > 0) {
        setDefaultLocations(defaultLocs);
      }
      await loadBlockedDays();
      
      // Load calendar after stats are shown (non-blocking)
      setTimeout(() => {
        loadCalendarEvents(defaultLocs);
      }, 100);
    };
    initializeData();
  }, []);

  // Separate effect for calendar navigation — reuses locations already in state
  const calendarEpoch = useRef(0);
  useEffect(() => {
    if (calendarEpoch.current === 0) { calendarEpoch.current = 1; return; }
    loadCalendarEvents(defaultLocations);
  }, [currentDate.getTime(), calendarView, scheduleDateRange.from, scheduleDateRange.to]);

  // Smart realtime - only updates what changed (minimal egress)
  useSmartRealtime<Booking>(
    'bookings',
    (newBooking) => {
      // On INSERT: increment total bookings and reload calendar if visible
      setStats(prev => ({ ...prev, totalBookings: prev.totalBookings + 1 }));
      loadCalendarEvents(defaultLocations);
    },
    (updatedBooking) => {
      // On UPDATE: just reload calendar to show updated data
      loadCalendarEvents(defaultLocations);
    },
    (deletedId) => {
      // On DELETE: decrement total bookings and reload calendar
      setStats(prev => ({ ...prev, totalBookings: Math.max(0, prev.totalBookings - 1) }));
      loadCalendarEvents(defaultLocations);
    }
  );

  // Course sessions + attendees drive the schedule booked counts / trainer / interpreter.
  useSmartRealtime<any>(
    'course_session_attendees',
    () => scheduleCalendarReload(),
    () => scheduleCalendarReload(),
    () => scheduleCalendarReload()
  );
  useSmartRealtime<any>(
    'course_sessions',
    () => scheduleCalendarReload(),
    () => scheduleCalendarReload(),
    () => scheduleCalendarReload()
  );

  useSmartRealtime<any>(
    'customers',
    (newCustomer) => {
      // On INSERT: increment total customers
      setStats(prev => ({ ...prev, totalCustomers: prev.totalCustomers + 1 }));
    },
    () => {}, // Updates don't affect stats count
    (deletedId) => {
      // On DELETE: decrement total customers
      setStats(prev => ({ ...prev, totalCustomers: Math.max(0, prev.totalCustomers - 1) }));
    }
  );

  useSmartRealtime<any>(
    'certificate_reminders',
    () => {
      scheduleSilentDashboardStatsReload();
    },
    () => scheduleSilentDashboardStatsReload(),
    () => scheduleSilentDashboardStatsReload()
  );

  // Listen for booking deletion events from other components
  useEffect(() => {
    const handleBookingDeleted = () => {
      void loadDashboardData({ silent: true });
    };

    window.addEventListener('bookingDeleted', handleBookingDeleted);
    
    return () => {
      window.removeEventListener('bookingDeleted', handleBookingDeleted);
    };
  }, []);

  useEffect(() => {
    setDayListModalSelectedId(null);
  }, [calendarDayListModal]);

  const loadBlockedDays = async () => {
    try {
      const { data, error } = await supabase
        .from('booking_blocked_days')
        .select('id, blocked_date, severity, reason')
        .order('blocked_date', { ascending: true });
      if (error) throw error;
      const map: Record<string, { id: string; severity: 'warn' | 'block'; reason: string }> = {};
      (data || []).forEach((row: any) => {
        map[String(row.blocked_date)] = {
          id: row.id,
          severity: row.severity === 'block' ? 'block' : 'warn',
          reason: row.reason || ''
        };
      });
      setBlockedDaysByDate(map);
    } catch (err) {
      console.error('Error loading blocked days:', err);
    }
  };

  const loadDefaultLocations = async (): Promise<Location[]> => {
    try {
      // Load locations directly from the locations table where customer_id matches the default training org customer
      // First find that customer ID
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('id, company_name')
        .ilike('company_name', BRAND.customerNameSearchPattern)
        .limit(1);

      if (customerError) {
        console.error('❌ Error finding default training customer:', customerError);
        return [];
      }

      if (!customerData || customerData.length === 0) {
        return [];
      }

      const defaultTrainingCustomerId = customerData[0].id;

      // Load locations directly from locations table
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('customer_id', defaultTrainingCustomerId);

      if (error) {
        console.error('❌ Error loading default locations:', error);
        return [];
      }

      if (data && data.length > 0) {
        const locations = data.map((loc: any) => mapLocationDbRow(loc));
        
        setDefaultLocations(locations);
        return locations;
      }
      return [];
    } catch (error) {
      console.error('❌ Error loading default locations:', error);
      return [];
    }
  };

  /** Dashboard calendar: merges legacy Booking rows + course_sessions into CalendarEvent rows (see src/domain/saasDataOwnership.ts). */
  const loadCalendarEvents = async (defaultLocs?: Location[]) => {
    try {
      // Only show loading for significant data changes, not for quick navigation
      const shouldShowLoading = calendarEvents.length === 0;
      if (shouldShowLoading) {
        setIsLoadingCalendar(true);
      }
      
      let startDate: string;
      let endDate: string;

      if (hasActiveScheduleDateRange(scheduleDateRange)) {
        startDate = scheduleDateRange.from;
        endDate = scheduleDateRange.to;
      } else if (calendarView === 'month') {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        
        // Create dates at noon local time to avoid timezone issues
        const startOfMonth = new Date(year, month, 1, 12, 0, 0);
        const endOfMonth = new Date(year, month + 1, 0, 12, 0, 0);
        
        // Format as YYYY-MM-DD without timezone conversion
        startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(endOfMonth.getDate()).padStart(2, '0')}`;
      } else if (calendarView === 'week') {
        const startOfWeek = weekStartDate;
        const endOfWeek = noonDate(
          startOfWeek.getFullYear(),
          startOfWeek.getMonth(),
          startOfWeek.getDate() + 6
        );
        startDate = isoDayKey(startOfWeek);
        endDate = isoDayKey(endOfWeek);
      } else { // day view
        startDate = currentDate.toISOString().split('T')[0];
        endDate = startDate;
      }


      // First, find all booking IDs that have dates in the visible range
      const { data: bookingDatesInRange, error: datesError } = await supabase
        .from('booking_dates')
        .select('booking_id')
        .gte('date', startDate)
        .lte('date', endDate);

      if (datesError) throw datesError;

      // Get unique booking IDs from booking_dates
      const bookingIdsFromDates = [...new Set((bookingDatesInRange || []).map(bd => bd.booking_id))];

      // Also get bookings where course_date is in range (for bookings without booking_dates entries)
      const { data: bookingsInRange, error: bookingsRangeError } = await supabase
        .from('bookings')
        .select('id')
        .gte('course_date', startDate)
        .lte('course_date', endDate);

      if (bookingsRangeError) throw bookingsRangeError;

      const bookingIdsFromCourseDate = (bookingsInRange || []).map(b => b.id);

      // Combine both sets of booking IDs
      const allBookingIds = [...new Set([...bookingIdsFromDates, ...bookingIdsFromCourseDate])];

      let bookingsData: any[] = [];
      if (allBookingIds.length > 0) {
        const { data, error: bookingsError } = await supabase
          .from('bookings')
          .select(`
            id,
            course_name,
            course_date,
            start_time,
            end_time,
            total_price,
            status,
            customer_id,
            instructor_id,
            location_id,
            contact_id,
            notes,
            attendee_count,
            instructor_price,
            instructor_po,
            client_po,
            booking_type,
            booking_dates (
              id,
              booking_id,
              date,
              start_time,
              end_time
            )
          `)
          .in('id', allBookingIds)
          .order('course_date', { ascending: true });
        if (bookingsError) throw bookingsError;
        bookingsData = data || [];
      }

      // Batch-load public booking clients for ALL bookings in one query (not N+1)
      const { supabasePublicBookingClientsStorage } = await import('../services/supabaseStorage');
      const allBookingIdsForClients = (bookingsData || []).map((b: any) => b.id);
      const publicClientsMap = await supabasePublicBookingClientsStorage.getPublicBookingClientsBatch(allBookingIdsForClients);

      const bookingsWithPublicClients = (bookingsData || []).map((booking: any) => {
        const publicBookingClients = publicClientsMap.get(booking.id) || [];
        const isPublicByType = booking.booking_type === 'public';
        const detectedBookingType = isPublicByType || publicBookingClients.length > 0
          ? 'public'
          : 'standard';

        return {
          id: booking.id,
          courseName: booking.course_name,
          courseDate: booking.course_date,
          startTime: booking.start_time,
          endTime: booking.end_time,
          totalPrice: booking.total_price,
          status: booking.status?.toLowerCase() || 'scheduled',
          customerId: booking.customer_id,
          instructorId: booking.instructor_id,
          locationId: booking.location_id,
          contactId: booking.contact_id,
          notes: booking.notes,
          attendeeCount: booking.attendee_count,
          instructorPrice: booking.instructor_price,
          instructorPO: booking.instructor_po,
          clientPO: booking.client_po,
          bookingType: detectedBookingType,
          publicBookingClients: publicBookingClients.length > 0 ? publicBookingClients : undefined,
          allBookingDates: booking.booking_dates?.map((bd: any) => bd.date) || [],
          allBookingDatesWithTimes: booking.booking_dates?.map((bd: any) => ({
            date: bd.date,
            startTime: bd.start_time,
            endTime: bd.end_time
          })) || []
        };
      });

      const bookings = bookingsWithPublicClients;
      const bookingRefIndexById: Record<string, number> = {};
      [...bookings]
        .sort((a, b) => {
          const dateCmp = String(a.courseDate || '').localeCompare(String(b.courseDate || ''));
          if (dateCmp !== 0) return dateCmp;
          return String(a.id).localeCompare(String(b.id));
        })
        .forEach((b, idx) => {
          bookingRefIndexById[b.id] = idx + 1;
        });

      // Load only customers and instructors that are referenced in visible bookings
      // Filter out null/undefined customerIds (for public bookings) before loading customers
      const uniqueCustomerIds = [...new Set(bookings.map(b => b.customerId).filter((id): id is string => id != null))];
      const uniqueInstructorIds = [...new Set(bookings.map(b => b.instructorId).filter(Boolean))];

      const [customers, instructors] = await Promise.all([
        (async () => {
          if (uniqueCustomerIds.length === 0) return [];
          try {
            const { data, error } = await supabase
              .from('customers')
              .select(`
                id,
                company_name,
                contacts (
                  id,
                  first_name,
                  last_name,
                  email,
                  phone_number
                ),
                locations (
                  id,
                  address,
                  city,
                  county,
                  postcode,
                  country
                )
              `)
              .in('id', uniqueCustomerIds);
            
            if (error) {
              console.error('Error loading customers for calendar:', error);
              return [];
            }

            return (data || []).map(c => ({
              id: c.id,
              companyName: c.company_name,
              contacts: (c.contacts || []).map((contact: any) => ({
                id: contact.id,
                firstName: contact.first_name,
                lastName: contact.last_name,
                email: contact.email,
                phoneNumber: contact.phone_number
              })),
              locations: (c.locations || []).map((loc: any) => mapLocationDbRow(loc))
            }));
          } catch (error) {
            console.error('Error loading customers for calendar:', error);
            return [];
          }
        })(),
        (async () => {
          const { supabaseInstructorStorage } = await import('../services/supabaseStorage');
          return supabaseInstructorStorage.getInstructors(500, 0);
        })()
      ]);
      
      // Store customers in state for use in other functions
      setCustomers(customers);
      
      // Generate calendar events from bookings
      const events: CalendarEvent[] = [];
      
      // Create a mutable customers list that we can extend with public booking clients' customers
      let extendedCustomers = [...customers];
      
      for (const booking of bookings) {
        // Get all dates for this booking: primary date + additional dates
        const allBookingDates: string[] = [];
        
        // Add primary course date if it exists
        if (booking.courseDate) {
          allBookingDates.push(booking.courseDate);
        }
        
        // Add additional dates from allBookingDates if they exist
        if (booking.allBookingDates && booking.allBookingDates.length > 0) {
          booking.allBookingDates.forEach(date => {
            if (date && !allBookingDates.includes(date)) {
              allBookingDates.push(date);
            }
          });
        }
        
        // Remove duplicates and sort
        const uniqueDates = [...new Set(allBookingDates)].sort();
        
        // Filter dates to only include those in our date range
        const datesInRange = uniqueDates.filter(dateStr => 
          dateStr >= startDate && dateStr <= endDate
        );
        
        if (datesInRange.length === 0) continue;
        
        // Get related data for this booking (skip customer lookup for public bookings)
        // Check if booking is public: bookingType is 'public', has publicBookingClients, OR customerId is null/undefined
        const isPublicBooking = booking.bookingType === 'public' || 
                               (booking.publicBookingClients && booking.publicBookingClients.length > 0) ||
                               (!booking.customerId && booking.bookingType !== 'standard');
        const customer = isPublicBooking ? null : (booking.customerId ? customers.find(c => c.id === booking.customerId) : undefined);
        const instructor = instructors.find(i => i.id === booking.instructorId);
        const contact = customer?.contacts.find(c => c.id === booking.contactId);
        
        // Handle public bookings - load all clients and their customer data
        let publicBookingClients: any[] = [];
        let publicBookingCustomerNames: string[] = [];
        
        if (booking.bookingType === 'public' && booking.publicBookingClients && booking.publicBookingClients.length > 0) {
          publicBookingClients = booking.publicBookingClients;
          
          // Load customer names for all public booking clients
          // First, find customers already loaded, then load missing ones
          const customerIdsForPublicClients = publicBookingClients.map((client: any) => client.customerId).filter(Boolean);
          const loadedPublicCustomers = customers.filter((c: any) => customerIdsForPublicClients.includes(c.id));
          const missingCustomerIds = customerIdsForPublicClients.filter((id: string) => !customers.some((c: any) => c.id === id));
          
          // Batch-load missing customers in a single query
          if (missingCustomerIds.length > 0) {
            try {
              const { data: missingData } = await supabase
                .from('customers')
                .select('id, company_name')
                .in('id', missingCustomerIds);
              (missingData || []).forEach((c: any) => {
                if (!extendedCustomers.some(ec => ec.id === c.id)) {
                  extendedCustomers.push({ id: c.id, companyName: c.company_name, contacts: [], locations: [] });
                }
              });
            } catch (error) {
              console.error('Error batch-loading customers for public bookings:', error);
            }
          }
          
          // Now map customer names using extended customers list
          publicBookingCustomerNames = publicBookingClients.map((client: any) => {
            const clientCustomer = extendedCustomers.find((c: any) => c.id === client.customerId);
            return clientCustomer?.companyName || 'Unknown Company';
          });
          
        } else if (booking.bookingType === 'public') {
          // Public booking but clients not loaded yet - still show as public
        }
        
        // Check both customer locations AND default training center locations
        // For public bookings, also check locations from public booking clients
        let customerLocation = customer?.locations.find(l => l.id === booking.locationId);
        
        // If public booking and location not found, check client locations
        if (isPublicBooking && !customerLocation && booking.locationId && publicBookingClients.length > 0) {
          for (const client of publicBookingClients) {
            const clientCustomer = extendedCustomers.find((c: any) => c.id === client.customerId);
            if (clientCustomer?.locations) {
              const foundLocation = clientCustomer.locations.find((l: any) => l.id === booking.locationId);
              if (foundLocation) {
                customerLocation = foundLocation;
                break;
              }
            }
          }
        }
        
        const defaultLocation = (defaultLocs || defaultLocations).find(l => l.id === booking.locationId);
        const location = customerLocation || defaultLocation;
        
        // Location lookup - only log error for non-public bookings or if it's a critical issue
        if (!location && booking.locationId && !isPublicBooking) {
          const currentDefaultLocs = defaultLocs || defaultLocations;
          console.error('❌ LOCATION NOT FOUND in DashboardModule:', {
            bookingId: booking.id,
            customerName: customer?.companyName,
            bookingLocationId: booking.locationId,
            customerLocationIds: customer?.locations?.map(l => l.id) || [],
            defaultLocationIds: currentDefaultLocs.map(l => l.id),
            allAvailableLocationIds: [...(customer?.locations?.map(l => l.id) || []), ...currentDefaultLocs.map(l => l.id)]
          });
        }
        
        // Create one calendar event for EACH date this booking occurs
        for (const dateStr of datesInRange) {
          // Get the correct start/end time for this specific date
          let correctStartTime = booking.startTime;
          let correctEndTime = booking.endTime;
          
          if (booking.allBookingDatesWithTimes && booking.allBookingDatesWithTimes.length > 0) {
            const dateEntry = booking.allBookingDatesWithTimes.find(item => item.date === dateStr);
            if (dateEntry) {
              correctStartTime = dateEntry.startTime || booking.startTime;
              correctEndTime = dateEntry.endTime || booking.endTime;
            }
          }
          
          // Determine title and company name based on booking type
          let eventTitle: string;
          let eventCompanyName: string;
          
          if (booking.bookingType === 'public') {
            // Public booking: always show "Public: CourseName" even if clients aren't loaded yet
            if (publicBookingClients && publicBookingClients.length > 0) {
              eventTitle = `Public: ${booking.courseName} (${publicBookingClients.length} client${publicBookingClients.length !== 1 ? 's' : ''})`;
              eventCompanyName = publicBookingCustomerNames.length > 0 
                ? publicBookingCustomerNames.join(', ') 
                : `${publicBookingClients.length} client${publicBookingClients.length !== 1 ? 's' : ''}`;
            } else {
              // Public booking but clients not loaded yet - still show as public
              eventTitle = `Public: ${booking.courseName}`;
              eventCompanyName = 'Public Booking';
            }
          } else {
            // Standard booking: show company name and course
            eventTitle = `${customer?.companyName || 'Unknown'} - ${booking.courseName}`;
            eventCompanyName = customer?.companyName || 'Unknown';
          }
          
          events.push({
            id: `${booking.id}-${dateStr}`, // Unique ID for each day's event
            title: eventTitle,
            date: new Date(dateStr + 'T12:00:00'), // Use noon to avoid timezone issues
            startTime: correctStartTime,
            endTime: correctEndTime,
            type: 'booking',
            companyName: eventCompanyName,
            courseName: booking.courseName,
            attendeeCount: booking.attendeeCount,
            status: booking.status,
            resource: {
              customerId: booking.customerId,
              bookingId: booking.id,
              instructorId: booking.instructorId,
              isPublicBooking: booking.bookingType === 'public' || !!publicBookingClients?.length,
              publicBookingClients: (booking.bookingType === 'public' || publicBookingClients?.length) ? publicBookingClients : undefined,
              bookingType: booking.bookingType || (publicBookingClients?.length ? 'public' : 'standard'),
              locationName: location ? (() => {
                const addressParts = [
                  location.addressLine1,
                  location.addressLine2,
                  location.addressLine3,
                  location.city,
                  location.eircodePostcode
                ].filter(part => part && part.trim() && part.trim().length > 0);

                let fullAddress = addressParts.join(', ');
                if (location.country) {
                  fullAddress += `, ${location.country}`;
                }
                return fullAddress;
              })() : undefined,
              locationNotes: location?.locationNotes || undefined,
              locationShortLabel: location ? shortVenueLabel(location.city, location.addressLine1) : undefined,
              sessionReferenceCode: bookingReferenceCode(booking.id, bookingRefIndexById[booking.id]),
              contactName: contact ? `${contact.firstName} ${contact.lastName}` : undefined,
              contactEmail: contact?.email,
              contactPhone: contact?.phoneNumber,
              notes: booking.notes,
              customerPrice: booking.totalPrice,
              instructorName: instructor ? `${instructor.firstName} ${instructor.lastName}` : undefined,
              instructorEmail: instructor?.email,
              instructorPrice: booking.instructorPrice,
              instructorPO: booking.instructorPO,
              clientPO: booking.clientPO,
              allBookingDates: uniqueDates,
              currentDate: dateStr
            }
          });
        }
      }

      // Include new course sessions in calendar
      const { data: sessionsData } = await supabase
        .from('course_sessions')
        .select('id, tenant_id, course_name, language, status, instructor_id, interpreter_id, location_id, capacity, date_blocks, notes, delivery_mode, session_other_info, documents_prep_done, certificates_prep_done, created_at')
        .eq('tenant_id', DEFAULT_TENANT_ID);
      const sessions = (sessionsData || []).filter((s: any) => (s.tenant_id ? s.tenant_id === DEFAULT_TENANT_ID : true));
      const sessionRefIndexById: Record<string, number> = {};
      [...sessions]
        .sort((a: any, b: any) => {
          const aDate = Array.isArray(a.date_blocks) && a.date_blocks[0]?.date ? String(a.date_blocks[0].date) : '';
          const bDate = Array.isArray(b.date_blocks) && b.date_blocks[0]?.date ? String(b.date_blocks[0].date) : '';
          const dateCmp = aDate.localeCompare(bDate);
          if (dateCmp !== 0) return dateCmp;
          return String(a.id).localeCompare(String(b.id));
        })
        .forEach((s: any, idx: number) => {
          sessionRefIndexById[s.id] = Object.keys(bookingRefIndexById).length + idx + 1;
        });
      // Reuse the instructors already fetched above for bookings
      const sessionInstructorIds = [...new Set((sessions || []).map((s: any) => s.instructor_id).filter(Boolean))];
      let sessionInstructorById: Record<string, { firstName: string; lastName: string; email: string }> = {};
      sessionInstructorIds.forEach((id: string) => {
        const i = instructors.find((x: any) => x.id === id);
        if (i) sessionInstructorById[id] = { firstName: i.firstName, lastName: i.lastName, email: i.email };
      });

      const defaultLocList = defaultLocs || defaultLocations;
      const sessionLocationIds = [...new Set((sessions || []).map((s: any) => s.location_id).filter(Boolean))];
      const missingLocationIds = sessionLocationIds.filter((id) => !defaultLocList.some((l) => l.id === id));
      const trainingLocationLabelById: Record<string, string> = {};
      defaultLocList.forEach((l) => {
        trainingLocationLabelById[l.id] = [l.addressLine1, l.city, l.country].filter(Boolean).join(', ') || l.addressLine1;
      });
      const extraLocationById: Record<string, Location> = {};
      if (missingLocationIds.length > 0) {
        const { data: locRows } = await supabase.from('locations').select('*').in('id', missingLocationIds);
        (locRows || []).forEach((row: any) => {
          const label =
            (row.name && String(row.name).trim()) ||
            [row.address_line_1 || row.address, row.city].filter(Boolean).join(', ') ||
            '';
          trainingLocationLabelById[row.id] = label;
          extraLocationById[row.id] = {
            id: row.id,
            addressLine1: row.address_line_1 || row.address || '',
            addressLine2: row.address_line_2 || '',
            addressLine3: row.address_line_3 || '',
            city: row.city || '',
            countyState: row.county_state || '',
            eircodePostcode: row.eircode_postcode || row.postcode || '',
            country: row.country || ''
          };
        });
      }

      const attendeeCountsBySession: Record<string, number> = {};
      {
        const pageSize = 1000;
        let from = 0;
        for (;;) {
          const { data: page, error: attendeePageError } = await supabase
            .from('course_session_attendees')
            .select('booking_id,status')
            .range(from, from + pageSize - 1);
          if (attendeePageError) throw attendeePageError;
          const rows = page || [];
          rows.forEach((a: any) => {
            if (!isAttendeeCountedInBookingTotal({ status: a.status || 'booked', isVirtual: false })) return;
            const sid = a.booking_id;
            attendeeCountsBySession[sid] = (attendeeCountsBySession[sid] || 0) + 1;
          });
          if (rows.length < pageSize) break;
          from += pageSize;
        }
      }

      let courseFeeMap: Record<string, number> = {};
      try {
        courseFeeMap = await fetchCourseFeeOverrideMap();
      } catch {
        courseFeeMap = {};
      }

      const sessionInterpreterIds = [
        ...new Set(
          (sessions || []).map((s: any) => s.interpreter_id).filter((id: unknown): id is string => typeof id === 'string' && !!id)
        )
      ];
      let interpreterMap: Awaited<ReturnType<typeof interpretersService.getByIds>> = {};
      if (sessionInterpreterIds.length > 0) {
        try {
          interpreterMap = await interpretersService.getByIds(sessionInterpreterIds);
        } catch {
          interpreterMap = {};
        }
      }

      (sessions || []).forEach((session: any) => {
        const blocks = Array.isArray(session.date_blocks) ? session.date_blocks : [];
        const booked = attendeeCountsBySession[session.id] || 0;
        const inst = session.instructor_id ? sessionInstructorById[session.instructor_id] : undefined;
        const catalogFee = getCourseCatalogDefaultFeeForSession(
          String(session.course_name || '').trim(),
          courseFeeMap,
          session.language
        );
        const adminCoursePrice = catalogFee > 0 ? catalogFee : undefined;
        const locFromDefault = session.location_id
          ? defaultLocList.find((l) => l.id === session.location_id)
          : undefined;
        const locExtra = session.location_id ? extraLocationById[session.location_id] : undefined;
        const location = locFromDefault || locExtra;
        const sessionLocLine = session.location_id
          ? trainingLocationLabelById[session.location_id] ||
            (location
              ? [location.addressLine1, location.city, location.country].filter(Boolean).join(', ')
              : undefined)
          : undefined;
        const interpRow = session.interpreter_id ? interpreterMap[session.interpreter_id] : undefined;
        const interpName = interpRow?.name;
        blocks.forEach((block: any) => {
          const dateStr = block?.date;
          if (!dateStr || dateStr < startDate || dateStr > endDate) return;
          const sessionStatus = session.status === 'cancelled' ? 'cancelled' : 'scheduled';
          events.push({
            id: `session-${session.id}-${dateStr}`,
            title: session.course_name,
            date: new Date(`${dateStr}T12:00:00`),
            startTime: block?.startTime || undefined,
            endTime: block?.endTime || undefined,
            type: 'booking',
            companyName: session.course_name,
            courseName: `${session.language || 'No language'} | ${booked} booked`,
            attendeeCount: booked,
            status: sessionStatus,
            resource: {
              courseSessionId: session.id,
              instructorId: session.instructor_id || undefined,
              instructorName: inst ? `${inst.firstName} ${inst.lastName}` : undefined,
              instructorEmail: inst?.email,
              locationName: sessionLocLine,
              locationNotes: location?.locationNotes || undefined,
              locationShortLabel: location ? shortVenueLabel(location.city, location.addressLine1) : undefined,
              sessionReferenceCode: sessionReferenceCode(session.id, sessionRefIndexById[session.id]),
              notes: `Capacity ${session.capacity} | Booked ${booked}`,
              sessionNotes: session.notes?.trim() ? session.notes : undefined,
              capacity: session.capacity,
              bookedCount: booked,
              websiteBookingActive: true,
              deliveryMode: session.delivery_mode ?? null,
              customerPrice: adminCoursePrice,
              interpreterName: interpName,
              sessionLanguage: session.language?.trim() || undefined,
              documentsPrepDone: session.documents_prep_done ?? false,
              certificatesPrepDone: session.certificates_prep_done ?? false,
              sessionOtherInfo: session.session_other_info?.trim() || undefined,
            }
          });
        });
      });

      setCalendarEvents(events);
    } catch (error) {
      console.error('Error loading calendar events:', error);
    } finally {
      // Always reset loading state
      setIsLoadingCalendar(false);
    }
  };

  const clearScheduleDateRange = () => setScheduleDateRange({ from: '', to: '' });

  const dateRangeActive = hasActiveScheduleDateRange(scheduleDateRange);

  const handleSetCalendarView = (view: CalendarView) => {
    clearScheduleDateRange();
    const prevView = calendarView;
    if (view === 'week') {
      const monday = mondayOfWeekContaining(startOfMonth(currentDate));
      setWeekStartDate(monday);
      setCurrentDate(monday);
    } else if (view === 'day') {
      const monday =
        prevView === 'week' ? weekStartDate : mondayOfWeekContaining(startOfMonth(currentDate));
      setWeekStartDate(monday);
      setCurrentDate(monday);
    }
    setCalendarView(view);
  };

  const navigate = (direction: 'prev' | 'next') => {
    if (isLoadingCalendar || dateRangeActive) return;

    const calendarElement = document.getElementById('training-calendar');
    const scrollPosition = calendarElement?.offsetTop || 0;

    const delta = direction === 'next' ? 1 : -1;
    let newDate = new Date(currentDate);

    if (calendarView === 'month') {
      clearScheduleDateRange();
      newDate = noonDate(currentDate.getFullYear(), currentDate.getMonth() + delta, 1);
    } else if (calendarView === 'week') {
      clearScheduleDateRange();
      const nextMonday = noonDate(
        weekStartDate.getFullYear(),
        weekStartDate.getMonth(),
        weekStartDate.getDate() + delta * 7
      );
      setWeekStartDate(nextMonday);
      newDate = nextMonday;
    } else {
      clearScheduleDateRange();
      newDate = noonDate(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + delta
      );
    }

    setCurrentDate(newDate);
    
    // Maintain scroll position smoothly after navigation
    requestAnimationFrame(() => {
      window.scrollTo({
        top: scrollPosition - 100, // Offset for header
        behavior: 'smooth'
      });
    });
  };

  const getCalendarTitle = () => {
    if (dateRangeActive) {
      return `${formatShortUkDate(parseIsoDay(scheduleDateRange.from))} – ${formatShortUkDate(parseIsoDay(scheduleDateRange.to))}`;
    }
    if (calendarView === 'month') {
      return currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }
    if (calendarView === 'week') {
      const endOfWeek = noonDate(
        weekStartDate.getFullYear(),
        weekStartDate.getMonth(),
        weekStartDate.getDate() + 6
      );
      return `${formatShortUkDate(weekStartDate)} – ${formatShortUkDate(endOfWeek)}`;
    }
    return currentDate.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getCalendarDays = () => {
    if (dateRangeActive) {
      return eachDayInIsoRange(scheduleDateRange.from, scheduleDateRange.to).map((day) => ({
        date: day,
        isCurrentMonth: true,
        isToday: day.toDateString() === new Date().toDateString(),
      }));
    }
    if (calendarView === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      // Create dates at noon to avoid timezone issues
      const firstDay = new Date(year, month, 1, 12, 0, 0);
      const lastDay = new Date(year, month + 1, 0, 12, 0, 0);
      
      // Get the Monday of the week containing the first day
      const startCalendar = new Date(firstDay);
      const dayOfWeek = startCalendar.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      startCalendar.setDate(firstDay.getDate() + mondayOffset);
      
      // Generate 42 days (6 weeks)
      const days = [];
      for (let i = 0; i < 42; i++) {
        // Create each day at noon to avoid timezone shifts
        const day = new Date(startCalendar.getFullYear(), startCalendar.getMonth(), startCalendar.getDate() + i, 12, 0, 0);
        days.push({
          date: day,
          isCurrentMonth: day.getMonth() === month,
          isToday: day.toDateString() === new Date().toDateString()
        });
      }
      return days;
    } else if (calendarView === 'week') {
      const startOfWeek = weekStartDate;
      const days = [];
      for (let i = 0; i < 7; i++) {
        const day = noonDate(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate() + i);
        days.push({
          date: day,
          isCurrentMonth: true,
          isToday: day.toDateString() === new Date().toDateString()
        });
      }
      return days;
    } else { // day view
      return [{
        // Ensure day view date is at noon to avoid timezone issues
        date: new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 12, 0, 0),
        isCurrentMonth: true,
        isToday: currentDate.toDateString() === new Date().toDateString()
      }];
    }
  };

  const getEventsForPeriod = (start: Date, end: Date) => {
    const all: CalendarEvent[] = [];
    let cur = new Date(start);
    const endTime = end.getTime();
    while (cur.getTime() <= endTime) {
      all.push(...getEventsForDay(cur));
      cur = noonDate(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);
    }
    return sortCalendarEventsByDateTime(all);
  };

  const getSchedulePeriodBounds = (): { start: Date; end: Date; label: string } => {
    if (dateRangeActive) {
      const start = parseIsoDay(scheduleDateRange.from);
      const end = parseIsoDay(scheduleDateRange.to);
      return {
        start,
        end,
        label: `${formatShortUkDate(start)} – ${formatShortUkDate(end)}`,
      };
    }
    if (calendarView === 'week') {
      const start = weekStartDate;
      const end = noonDate(start.getFullYear(), start.getMonth(), start.getDate() + 6);
      return { start, end, label: `${formatShortUkDate(start)} – ${formatShortUkDate(end)}` };
    }
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    return {
      start,
      end,
      label: currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
    };
  };

  const getEventsForDay = (date: Date) => {
    // Use local date string to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    const eventsForDay = calendarEvents.filter(event => {
      let eventDateStr;
      if (event.date instanceof Date) {
        const year = event.date.getFullYear();
        const month = String(event.date.getMonth() + 1).padStart(2, '0');
        const day = String(event.date.getDate()).padStart(2, '0');
        eventDateStr = `${year}-${month}-${day}`;
      } else {
        // Parse string date safely
        const dateString = String(event.date);
        if (dateString.includes('T')) {
          eventDateStr = dateString.split('T')[0];
        } else {
          eventDateStr = dateString;
        }
      }
      return eventDateStr === dateStr;
    });
    
    return eventsForDay;
  };

  const getDisplayEventsForDay = (date: Date, gridView: 'month' | 'week' = 'week') => {
    const events = getEventsForDay(date);
    if (calendarView === 'day' && !dateRangeActive) {
      return {
        displayEvents: events,
        hasMore: false,
        moreCount: 0,
        totalInDay: events.length,
      };
    }
    const maxEvents = maxVisibleEvents(gridView);
    const displayEvents = events.slice(0, maxEvents);
    const hasMore = events.length > maxEvents;
    const moreCount = hasMore ? events.length - maxEvents : 0;
    return { displayEvents, hasMore, moreCount, totalInDay: events.length };
  };

  const handleDateClick = (date: Date) => {
    const fixedDate = noonDate(date.getFullYear(), date.getMonth(), date.getDate());
    const calendarElement = document.getElementById('training-calendar');
    const scrollPosition = calendarElement?.offsetTop || 0;

    clearScheduleDateRange();
    setWeekStartDate(mondayOfWeekContaining(fixedDate));
    setCurrentDate(fixedDate);
    setCalendarView('day');
    
    // Maintain scroll position after view change
    setTimeout(() => {
      window.scrollTo({
        top: scrollPosition - 100, // Offset for header
        behavior: 'smooth'
      });
    }, 100);
  };

  const openBlockedDayModal = (date: Date) => {
    const key = isoDayKey(date);
    const existing = blockedDaysByDate[key];
    setBlockedDayModal({
      date,
      severity: existing?.severity || 'block',
      reason: existing?.reason || '',
      existingId: existing?.id
    });
  };

  const saveBlockedDay = async () => {
    if (!blockedDayModal) return;
    const dayKey = isoDayKey(blockedDayModal.date);
    try {
      const { error } = await supabase
        .from('booking_blocked_days')
        .upsert({
          blocked_date: dayKey,
          severity: blockedDayModal.severity,
          reason: blockedDayModal.reason.trim() || null
        });
      if (error) throw error;
      await loadBlockedDays();
      setBlockedDayModal(null);
      showSuccess('Blocked day saved.');
    } catch (err: any) {
      showError(err?.message || 'Could not save blocked day.');
    }
  };

  const removeBlockedDay = async () => {
    if (!blockedDayModal?.existingId) return;
    try {
      const { error } = await supabase
        .from('booking_blocked_days')
        .delete()
        .eq('id', blockedDayModal.existingId);
      if (error) throw error;
      await loadBlockedDays();
      setBlockedDayModal(null);
      showSuccess('Blocked day removed.');
    } catch (err: any) {
      showError(err?.message || 'Could not remove blocked day.');
    }
  };

  const blockedDayImpactedEvents = blockedDayModal
    ? getEventsForDay(blockedDayModal.date)
    : [];

  const handleBookingClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    if (event.resource?.courseSessionId) {
      if (onOpenSession) {
        onOpenSession(event.resource.courseSessionId);
      } else {
        onViewChange('bookings', { sessionId: event.resource.courseSessionId, fromCalendar: true });
      }
      return;
    }
    if (event.resource?.bookingId) {
      onViewChange('bookings', { bookingId: event.resource.bookingId, fromCalendar: true });
      return;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'reminder_email_sent':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  const StatCard = ({ 
    title, 
    value, 
    icon: Icon, 
    gradient, 
    onClick,
    urgent = false 
  }: {
    title: string;
    value: number;
    icon: React.ElementType;
    gradient: string;
    onClick?: () => void;
    urgent?: boolean;
  }) => (
    <div 
      className={`bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-4 transition-all duration-300 ${
        onClick ? 'cursor-pointer hover:shadow-xl hover:scale-105' : 'hover:shadow-xl'
      } ${urgent ? 'ring-2 ring-red-300 ring-opacity-50' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center space-x-3">
        <div className={`w-10 h-10 ${gradient} rounded-xl flex items-center justify-center shadow-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-600">{title}</p>
          <p className={`text-2xl font-bold ${urgent ? 'text-red-600' : 'text-slate-800'}`}>
            {formatNumber(value)}
          </p>
        </div>
      </div>
      {onClick && (
        <div className="mt-3 flex items-center text-blue-600 text-xs font-medium">
          View Details
          <ChevronRight className="w-3 h-3 ml-1" />
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Calendar - Prominently at Top */}
      <div id="training-calendar" className="bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {/* Enhanced Calendar Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 border-b border-blue-700">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Training Schedule</h2>
                <p className="text-blue-100 text-sm">Manage and view all your training sessions</p>
              </div>
            </div>
            
            {/* Calendar Controls */}
            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
              {/* View Selector - Enhanced */}
              <div className="flex bg-white/20 backdrop-blur-sm rounded-lg p-0.5">
                <button
                  onClick={() => handleSetCalendarView('month')}
                  disabled={dateRangeActive}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40 ${
                    !dateRangeActive && calendarView === 'month'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => handleSetCalendarView('week')}
                  disabled={dateRangeActive}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40 ${
                    !dateRangeActive && calendarView === 'week'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => handleSetCalendarView('day')}
                  disabled={dateRangeActive}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40 ${
                    !dateRangeActive && calendarView === 'day'
                      ? 'bg-white text-blue-600 shadow-md'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  Day
                </button>
              </div>

              {(dateRangeActive || calendarView === 'month' || calendarView === 'week') && (
                <label className="flex items-center gap-2 rounded-lg bg-white/15 px-2 py-1 text-xs font-medium text-white cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="rounded border-white/50 text-blue-600 focus:ring-white/40"
                    checked={calendarScheduleMode}
                    onChange={(e) => setCalendarScheduleMode(e.target.checked)}
                  />
                  Schedule table
                </label>
              )}
              
              {/* Navigation - Enhanced */}
              <div
                className={`flex items-center space-x-1 bg-white/20 backdrop-blur-sm rounded-lg px-2 py-1 ${
                  dateRangeActive ? 'opacity-40' : ''
                }`}
              >
                <button
                  onClick={() => navigate('prev')}
                  disabled={isLoadingCalendar || dateRangeActive}
                  className="p-1.5 hover:bg-white/20 active:bg-white/30 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 text-white" />
                </button>
                <span className={`text-sm font-semibold text-white min-w-[120px] sm:min-w-[180px] text-center transition-all duration-300 ${
                  isLoadingCalendar ? 'opacity-50' : ''
                }`}>
                  {isLoadingCalendar ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-white/30 border-t-white mr-2"></div>
                      Loading...
                    </div>
                  ) : (
                    getCalendarTitle()
                  )}
                </span>
                <button
                  onClick={() => navigate('next')}
                  disabled={isLoadingCalendar || dateRangeActive}
                  className="p-1.5 hover:bg-white/20 active:bg-white/30 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={() => {
                    clearScheduleDateRange();
                    const today = noonDate(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                    setWeekStartDate(mondayOfWeekContaining(today));
                    setCurrentDate(today);
                  }}
                  disabled={dateRangeActive}
                  className="ml-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-medium rounded-md transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Go to today"
                >
                  Today
                </button>
              </div>
              
              {/* Date-range picker — filters events in every Training
                  Schedule view. Replaces the old "View All Bookings" button. */}
              <div className="flex flex-wrap items-center gap-1.5 bg-white/15 backdrop-blur-sm rounded-lg px-2 py-1.5">
                <Calendar className="w-4 h-4 text-white/90" />
                <label className="flex items-center gap-1 text-xs font-medium text-white/90">
                  <span className="hidden sm:inline">From</span>
                  <input
                    type="date"
                    value={scheduleDateRange.from}
                    max={scheduleDateRange.to || undefined}
                    onChange={(e) => setScheduleDateRange((r) => ({ ...r, from: e.target.value }))}
                    className="rounded-md border border-white/30 bg-white/90 dark:bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-white/60"
                  />
                </label>
                <label className="flex items-center gap-1 text-xs font-medium text-white/90">
                  <span className="hidden sm:inline">To</span>
                  <input
                    type="date"
                    value={scheduleDateRange.to}
                    min={scheduleDateRange.from || undefined}
                    onChange={(e) => setScheduleDateRange((r) => ({ ...r, to: e.target.value }))}
                    className="rounded-md border border-white/30 bg-white/90 dark:bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-white/60"
                  />
                </label>
                {(scheduleDateRange.from || scheduleDateRange.to) && (
                  <button
                    type="button"
                    onClick={() => setScheduleDateRange({ from: '', to: '' })}
                    className="inline-flex items-center rounded-md bg-white/20 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-white/30"
                    title="Clear date range filter"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Calendar Content */}
        <div className="p-4 sm:p-6">

          {/* DAY VIEW — full-width spreadsheet table.
              The header already provides Prev / Today / Next + the new
              From/To range picker, so we intentionally do NOT pass
              onDateChange here — it would render a redundant "Select date"
              control inside the table. */}
          {calendarScheduleMode && (dateRangeActive || calendarView === 'month' || calendarView === 'week') && (() => {
            const period = getSchedulePeriodBounds();
            return (
              <DayScheduleTable
                periodLabel={period.label}
                events={getEventsForPeriod(period.start, period.end)}
                showDateColumn
                isLoading={isLoadingCalendar}
                onEventRowClick={handleBookingClick}
                onGoToBookings={() => onViewChange('bookings')}
              />
            );
          })()}

          {!dateRangeActive && calendarView === 'day' && (
            <DayScheduleTable
              forDate={noonDate(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())}
              events={getEventsForDay(
                noonDate(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())
              )}
              isLoading={isLoadingCalendar}
              onEventRowClick={handleBookingClick}
              onGoToBookings={() => onViewChange('bookings')}
              blockedDayStatus={blockedDaysByDate[isoDayKey(currentDate)] || null}
              onManageBlockedDay={openBlockedDayModal}
            />
          )}

          {/* Date-range / month / week grid */}
          {!calendarScheduleMode && (dateRangeActive || calendarView !== 'day') && (
          <>
          {/* Mobile: readable day list (week) or compact count grid (month) */}
          <div className="md:hidden space-y-2">
            {(calendarView === 'week' || dateRangeActive) ? (
              <div className="space-y-2">
                {getCalendarDays().map((dayData, index) => {
                  const dayKey = isoDayKey(dayData.date);
                  const blockedStatus = blockedDaysByDate[dayKey];
                  const allDayEvents = getEventsForDay(dayData.date);
                  const count = allDayEvents.length;
                  const dateLabel = dayData.date.toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  });
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        if (count > 0) {
                          setCalendarDayListModal({
                            date: noonDate(dayData.date.getFullYear(), dayData.date.getMonth(), dayData.date.getDate()),
                            events: allDayEvents,
                          });
                        } else {
                          handleDateClick(dayData.date);
                        }
                      }}
                      className={`flex w-full items-center gap-3 rounded-2xl border-2 bg-white p-3.5 text-left transition active:scale-[0.99] dark:bg-slate-800 ${blockedDayCellClasses(blockedStatus, { isToday: dayData.isToday })}`}
                    >
                      <div
                        className={`flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl ${
                          dayData.isToday
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100'
                        }`}
                      >
                        <span className="text-[10px] font-semibold uppercase leading-none opacity-80">
                          {dayData.date.toLocaleDateString('en-GB', { weekday: 'short' })}
                        </span>
                        <span className="text-lg font-bold leading-tight">{dayData.date.getDate()}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{dateLabel}</p>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {blockedStatus
                            ? formatBlockedDayCalendarLabel(blockedStatus)
                            : count === 0
                              ? 'No sessions'
                              : `${count} session${count === 1 ? '' : 's'}`}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        {count > 0 ? (
                          <span className="inline-flex min-w-[2rem] items-center justify-center rounded-full bg-blue-100 px-2.5 py-1 text-sm font-bold text-blue-700 dark:bg-blue-900/50 dark:text-blue-200">
                            {count}
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-slate-400">—</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                  <div key={`${d}-${i}`} className="py-1 text-center text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    {d}
                  </div>
                ))}
                {getCalendarDays().map((dayData, index) => {
                  const dayKey = isoDayKey(dayData.date);
                  const blockedStatus = blockedDaysByDate[dayKey];
                  const allDayEvents = getEventsForDay(dayData.date);
                  const count = allDayEvents.length;
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        if (count > 0) {
                          setCalendarDayListModal({
                            date: noonDate(dayData.date.getFullYear(), dayData.date.getMonth(), dayData.date.getDate()),
                            events: allDayEvents,
                          });
                        } else {
                          handleDateClick(dayData.date);
                        }
                      }}
                      className={`flex min-h-[3.25rem] flex-col items-center justify-center rounded-xl border px-0.5 py-1 transition ${
                        dayData.isToday
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40'
                          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'
                      } ${!dayData.isCurrentMonth ? 'opacity-40' : ''} ${blockedStatus ? 'ring-1 ring-amber-400' : ''}`}
                    >
                      <span
                        className={`text-sm font-bold ${
                          dayData.isToday ? 'text-blue-700 dark:text-blue-300' : 'text-slate-800 dark:text-slate-100'
                        }`}
                      >
                        {dayData.date.getDate()}
                      </span>
                      {count > 0 ? (
                        <span className="mt-0.5 rounded-full bg-blue-600 px-1.5 text-[9px] font-bold leading-4 text-white">
                          {count}
                        </span>
                      ) : (
                        <span className="mt-0.5 h-4" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="pt-1 text-center text-[11px] text-slate-500 dark:text-slate-400">
              Tap a day to view all sessions
            </p>
          </div>

          {/* Desktop / tablet: full week-month grid with event cards */}
          <div className={`hidden md:grid gap-1 sm:gap-2 transition-all duration-300 ease-in-out ${
            isLoadingCalendar ? 'opacity-50 scale-98' : 'opacity-100 scale-100'
          } ${
            calendarView === 'month' && !dateRangeActive ? 'grid-cols-7' :
            'grid-cols-7 bg-slate-50 dark:bg-slate-900/30 rounded-lg p-1'
          }`}>
          {/* Day Headers */}
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayName) => (
            <div key={dayName} className="text-center py-3 text-sm font-bold text-slate-700 dark:text-slate-200 uppercase bg-slate-100 dark:bg-slate-800/60 rounded-lg tracking-wide">
              {dayName}
            </div>
          ))}

          {/* Calendar Days */}
          {getCalendarDays().map((dayData, index) => {
            const dayKey = isoDayKey(dayData.date);
            const blockedStatus = blockedDaysByDate[dayKey];
            const allDayEvents = getEventsForDay(dayData.date);
            const gridView: 'month' | 'week' =
              dateRangeActive || calendarView === 'week' ? 'week' : 'month';
            const { displayEvents, hasMore, moreCount, totalInDay } = getDisplayEventsForDay(dayData.date, gridView);
            const linesBudget = linesBudgetForCard(gridView, totalInDay);

            return (
              <div
                key={index}
                className={`bg-white dark:bg-slate-800 rounded-lg p-2 sm:p-3 border-2 transition-all duration-200 ${blockedDayCellClasses(blockedStatus, { isToday: dayData.isToday })} ${
                  !dayData.isCurrentMonth && calendarView === 'month' && !dateRangeActive && !blockedStatus ? 'opacity-40 bg-slate-50 dark:bg-slate-800/50' : ''
                } ${
                  calendarView === 'week' || dateRangeActive ? 'min-h-[180px] sm:min-h-[200px]' : 'min-h-[110px] sm:min-h-[130px]'
                }`}
              >
                <button
                  type="button"
                  className="w-full text-center mb-2 rounded-lg border border-transparent hover:border-blue-200 dark:hover:border-blue-700 hover:bg-blue-50/50 dark:hover:bg-slate-700/50 py-1 transition-colors cursor-pointer"
                  onClick={() => handleDateClick(dayData.date)}
                >
                  <div className={`text-base sm:text-lg font-bold ${
                    dayData.isToday ? 'text-blue-700 dark:text-blue-300 bg-white dark:bg-slate-700 rounded-full w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center shadow-sm mx-auto' :
                    dayData.isCurrentMonth ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'
                  }`}>
                    {dayData.date.getDate()}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 font-medium mt-0.5">
                    {dayData.date.toLocaleDateString('en-GB', { weekday: 'short' })}
                  </div>
                </button>

                <div className="space-y-1">
                  {blockedStatus && (
                    <div className={`mb-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                      blockedStatus.severity === 'block'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                    }`} title={formatBlockedDayCalendarLabel(blockedStatus)}>
                      {formatBlockedDayCalendarLabel(blockedStatus)}
                    </div>
                  )}
                  {displayEvents.map((event, eventIndex) => (
                    <TrainingCalendarEventCard
                      key={`${event.id}-${eventIndex}`}
                      event={event}
                      linesBudget={linesBudget}
                      calendarView={gridView}
                      onBookingClick={handleBookingClick}
                    />
                  ))}

                  {hasMore && (
                    <div className="text-center py-0.5">
                      <button
                        type="button"
                        className="w-full text-xs font-semibold text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/35 rounded-lg px-2 py-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/55 transition-colors border border-blue-200 dark:border-blue-800 shadow-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCalendarDayListModal({
                            date: new Date(dayData.date.getFullYear(), dayData.date.getMonth(), dayData.date.getDate(), 12, 0, 0),
                            events: allDayEvents
                          });
                        }}
                      >
                        Expand list (+{moreCount} more)
                      </button>
                    </div>
                  )}

                  {displayEvents.length === 0 && (calendarView === 'week' || dateRangeActive) && (
                    <div className="text-center py-3">
                      <div className="text-xs text-slate-400 dark:text-slate-500">No sessions</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          </div>
          </>
          )}
          
          {calendarEvents.length === 0 && (
            <div className="text-center py-12 mt-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">No bookings scheduled</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 max-w-md mx-auto">
                Schedule your first training session to see it appear on the calendar.
              </p>
              <button
                onClick={() => onViewChange('bookings')}
                className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-semibold rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Create First Booking
              </button>
            </div>
          )}
        </div>
      </div>

      {calendarDayListModal && (
        <div
          className="fixed inset-0 z-[65] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4"
          onClick={() => setCalendarDayListModal(null)}
        >
          <div
            className="flex max-h-[90dvh] w-full flex-col rounded-t-3xl border border-slate-200 bg-white p-4 shadow-2xl sm:max-h-[85vh] sm:w-[min(96vw,1200px)] sm:rounded-2xl sm:p-6 dark:border-slate-700 dark:bg-slate-800 pb-[calc(1rem+env(safe-area-inset-bottom))]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-slate-300 sm:hidden" />
            <div className="flex shrink-0 items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Sessions for day</h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  {calendarDayListModal.date.toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
                <p className="mt-0.5 text-xs font-medium text-slate-500">
                  {calendarDayListModal.events.length} session
                  {calendarDayListModal.events.length === 1 ? '' : 's'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCalendarDayListModal(null)}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {calendarDayListModal.events.map((event, idx) => (
                  <TrainingCalendarEventCard
                    key={`${event.id}-${idx}`}
                    event={event}
                    linesBudget={6}
                    calendarView="week"
                    variant="tile"
                    isSelected={dayListModalSelectedId === event.id}
                    onSelect={() => setDayListModalSelectedId(event.id)}
                    onBookingClick={handleBookingClick}
                  />
                ))}
              </div>
            </div>
            <div className="mt-3 flex shrink-0 gap-2 border-t border-slate-100 pt-3 dark:border-slate-700">
              <button
                type="button"
                onClick={() => {
                  const d = calendarDayListModal.date;
                  setCalendarDayListModal(null);
                  handleDateClick(d);
                }}
                className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              >
                Open day view
              </button>
              <button
                type="button"
                onClick={() => setCalendarDayListModal(null)}
                className="flex-1 rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {blockedDayModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Manage blocked day</h3>
              <button
                type="button"
                onClick={() => setBlockedDayModal(null)}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {blockedDayModal.date.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Behavior</label>
                <select
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  value={blockedDayModal.severity}
                  onChange={(e) => setBlockedDayModal((p) => (p ? { ...p, severity: e.target.value as 'warn' | 'block' } : p))}
                >
                  <option value="block">Hard block</option>
                  <option value="warn">Warning only</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Reason</label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  placeholder="Public holiday, venue closed, etc."
                  value={blockedDayModal.reason}
                  onChange={(e) => setBlockedDayModal((p) => (p ? { ...p, reason: e.target.value } : p))}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Impacted sessions / attendees</label>
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 dark:border-slate-600 dark:bg-slate-700/40">
                  {blockedDayImpactedEvents.length === 0 ? (
                    <div className="text-xs text-slate-500 dark:text-slate-300">No sessions on this day.</div>
                  ) : (
                    blockedDayImpactedEvents.map((event) => {
                      const attendees = event.resource?.bookedCount ?? event.attendeeCount ?? 0;
                      return (
                        <button
                          key={event.id}
                          type="button"
                          onClick={() => {
                            if (event.resource?.courseSessionId) {
                              if (onOpenSession) {
                                onOpenSession(event.resource.courseSessionId);
                              } else {
                                onViewChange('bookings', { sessionId: event.resource.courseSessionId, fromCalendar: true });
                              }
                              return;
                            }
                            if (event.resource?.bookingId) {
                              onViewChange('bookings', { bookingId: event.resource.bookingId, fromCalendar: true });
                            }
                          }}
                          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1 text-left text-xs hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:hover:bg-slate-700"
                        >
                          <div className="font-semibold text-slate-800 dark:text-slate-100">{event.courseName || event.title}</div>
                          <div className="text-slate-500 dark:text-slate-300">{attendees} attendee{attendees === 1 ? '' : 's'}</div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <button
                type="button"
                onClick={() => void removeBlockedDay()}
                disabled={!blockedDayModal.existingId}
                className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Remove
              </button>
              <button
                type="button"
                onClick={() => void saveBlockedDay()}
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Section - Below Calendar */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          title="Total Clients"
          value={stats.totalCustomers}
          icon={Users}
          gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
          onClick={() => onViewChange('clients')}
        />
        
        <StatCard
          title="Certificates"
          value={stats.totalCertificates}
          icon={Award}
          gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
          onClick={() => onViewChange('certificates')}
        />
        
        <StatCard
          title="Reminders"
          value={stats.activeReminders}
          icon={Bell}
          gradient="bg-gradient-to-br from-amber-500 to-orange-600"
          onClick={() => onViewChange('reminders')}
          urgent={stats.activeReminders > 0}
        />
        
        <StatCard
          title="Reviews Due"
          value={stats.dueReviewReminders}
          icon={Star}
          gradient="bg-gradient-to-br from-purple-500 to-purple-600"
          onClick={() => onViewChange('reviews')}
          urgent={stats.dueReviewReminders > 0}
        />
        
        <StatCard
          title="Bookings"
          value={stats.totalBookings}
          icon={TrendingUp}
          gradient="bg-gradient-to-br from-green-500 to-emerald-600"
          onClick={() => onViewChange('bookings')}
        />
        
        <StatCard
          title="Unpaid"
          value={stats.unpaidCustomers}
          icon={Euro}
          gradient="bg-gradient-to-br from-red-500 to-red-600"
          onClick={() => onViewChange('clients', { filter: 'unpaid' })}
          urgent={stats.unpaidCustomers > 0}
        />
      </div>

      {/* Additional Stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <StatCard
          title="Total Locations"
          value={stats.totalLocations}
          icon={MapPin}
          gradient="bg-gradient-to-br from-slate-500 to-slate-600"
        />
        
        <StatCard
          title="Total Contacts"
          value={stats.totalContacts}
          icon={Mail}
          gradient="bg-gradient-to-br from-cyan-500 to-blue-600"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8">
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
          <Calendar className="w-6 h-6 mr-3 text-slate-600" />
          Quick Actions
        </h2>
        
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-5">
          <button
            onClick={() => onViewChange('certificates')}
            className="group bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-2xl p-6 border border-blue-200 transition-all duration-300 hover:shadow-lg hover:scale-105 text-left"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Generate Certificates</h3>
                <p className="text-sm text-slate-600">Upload data and create certificates</p>
              </div>
            </div>
            <div className="flex items-center text-blue-600 text-sm font-medium">
              Start Certificate Generation
              <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
          </button>

          <button
            onClick={() => onViewChange('clients')}
            className="group bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 rounded-2xl p-6 border border-emerald-200 transition-all duration-300 hover:shadow-lg hover:scale-105 text-left"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Manage Clients</h3>
                <p className="text-sm text-slate-600">Customer relationship management</p>
              </div>
            </div>
            <div className="flex items-center text-emerald-600 text-sm font-medium">
              View Client Database
              <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
          </button>

          <button
            onClick={() => onViewChange('ops')}
            className="group bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 rounded-2xl p-6 border border-amber-200 transition-all duration-300 hover:shadow-lg hover:scale-105 text-left"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Ops Day Planner</h3>
                <p className="text-sm text-slate-600">Sessions, status and risks</p>
              </div>
            </div>
            <div className="flex items-center text-amber-700 text-sm font-medium">
              Open Session Planner
              <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
          </button>

          <button
            onClick={() => setShowEmailTemplatesModal(true)}
            className="group bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 rounded-2xl p-6 border border-purple-200 transition-all duration-300 hover:shadow-lg hover:scale-105 text-left"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Email Templates</h3>
                <p className="text-sm text-slate-600">Manage email templates</p>
              </div>
            </div>
            <div className="flex items-center text-purple-600 text-sm font-medium">
              Manage Templates
              <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
          </button>
          <button
            onClick={() => setShowWhatsAppTemplatesModal(true)}
            className="group bg-gradient-to-r from-sky-50 to-cyan-50 hover:from-sky-100 hover:to-cyan-100 rounded-2xl p-6 border border-sky-200 transition-all duration-300 hover:shadow-lg hover:scale-105 text-left"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-cyan-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">WhatsApp Templates</h3>
                <p className="text-sm text-slate-600">Session message presets</p>
              </div>
            </div>
            <div className="flex items-center text-sky-700 text-sm font-medium">
              Manage templates
              <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
            </div>
          </button>
        </div>
      </div>

      {(stats.unpaidCustomers > 0 || stats.activeReminders > 0 || stats.dueReviewReminders > 0) && (
        <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-3xl shadow-xl border border-red-200 p-8">
          <h2 className="text-2xl font-bold text-red-900 mb-6 flex items-center">
            <AlertTriangle className="w-6 h-6 mr-3" />
            Urgent Actions Required
          </h2>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {stats.unpaidCustomers > 0 && (
              <button
                onClick={() => onViewChange('clients', { filter: 'unpaid' })}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-red-300 hover:shadow-lg hover:scale-105 transition-all duration-300 text-left group"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <Euro className="w-8 h-8 text-red-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-900">{stats.unpaidCustomers} Unpaid Client{stats.unpaidCustomers !== 1 ? 's' : ''}</h3>
                    <p className="text-sm text-red-700">Payment pending for certificates</p>
                  </div>
                </div>
                <div className="flex items-center text-red-600 text-sm font-medium">
                  Review Payments
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </button>
            )}

            {stats.activeReminders > 0 && (
              <button
                onClick={() => onViewChange('reminders')}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-amber-300 hover:shadow-lg hover:scale-105 transition-all duration-300 text-left group"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <Bell className="w-8 h-8 text-amber-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-amber-900">{stats.activeReminders} Certificate{stats.activeReminders !== 1 ? 's' : ''} Expiring</h3>
                    <p className="text-sm text-amber-700">Renewal reminders needed</p>
                  </div>
                </div>
                <div className="flex items-center text-amber-600 text-sm font-medium">
                  Send Reminders
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </button>
            )}

            {stats.dueReviewReminders > 0 && (
              <button
                onClick={() => onViewChange('reviews')}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-purple-300 hover:shadow-lg hover:scale-105 transition-all duration-300 text-left group"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <Star className="w-8 h-8 text-purple-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-purple-900">{stats.dueReviewReminders} Review{stats.dueReviewReminders !== 1 ? 's' : ''} Due</h3>
                    <p className="text-sm text-purple-700">Client feedback requests pending</p>
                  </div>
                </div>
                <div className="flex items-center text-purple-600 text-sm font-medium">
                  Request Reviews
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Email Templates Modal */}
      {showEmailTemplatesModal && (
        <ManageEmailTemplatesModal
          onClose={() => setShowEmailTemplatesModal(false)}
          onTemplatesUpdated={() => {
            setShowEmailTemplatesModal(false);
            showSuccess('Email templates updated successfully');
          }}
        />
      )}
      {showWhatsAppTemplatesModal && (
        <ManageWhatsAppTemplatesModal
          onClose={() => setShowWhatsAppTemplatesModal(false)}
          onTemplatesUpdated={() => {
            setShowWhatsAppTemplatesModal(false);
            showSuccess('WhatsApp templates updated successfully');
          }}
        />
      )}

      {/* Getting Started Guide */}
      {stats.totalCustomers === 0 && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl shadow-xl border border-blue-200 p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl shadow-xl mb-6">
            <FileText className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-4">Welcome to Rapid Cert!</h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Get started by adding your first client or uploading certificate data to begin managing your training business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => onViewChange('clients')}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 btn-enhanced hover-lift"
            >
              <Users className="w-5 h-5 mr-2" />
              Add Your First Client
            </button>
            <button
              onClick={() => onViewChange('certificates')}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 btn-secondary-enhanced hover-lift"
            >
              <Award className="w-5 h-5 mr-2" />
              Generate Certificates
            </button>
          </div>
        </div>
      )}
    </div>
  );
}